import { useState, useEffect } from 'react';
import { addReaction, fetchFootprintDetail } from '../../services/footprintService';
import { consumeReaction, getRemainingReactions } from '../../utils/reactionUtils';
import { getRemainingMs } from '../../hooks/useFootprints';

const formatRemaining = (post) => {
  const rem = getRemainingMs(post);
  if (rem <= 0) return '消滅済み';
  const h = Math.floor(rem / 3600000);
  if (h < 1) return `あと${Math.floor(rem / 60000)}分`;
  return `あと${h}時間`;
};

/** 画像1枚分のローディング付き表示 */
const ImageWithLoader = ({ src, alt, className, containerClassName }) => {
  const [loaded, setLoaded] = useState(false);
  return (
    <div className={`relative ${containerClassName ?? ''}`}>
      {!loaded && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-300 border-t-amber-500" />
        </div>
      )}
      <img
        src={src}
        alt={alt}
        className={className}
        onLoad={() => setLoaded(true)}
        style={{ opacity: loaded ? 1 : 0, transition: 'opacity 0.2s' }}
      />
    </div>
  );
};

const FootprintDetailSheet = ({ post, onClose, onReaction }) => {
  const [reactionCount, setReactionCount] = useState(post.reactionCount ?? 0);
  const [remaining, setRemaining] = useState(getRemainingReactions());
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(true);

  // 表示中: 'photo' | 'sign'
  const [view, setView] = useState('photo');

  useEffect(() => {
    fetchFootprintDetail(post.postId)
      .then(setDetail)
      .catch(() => {})
      .finally(() => setDetailLoading(false));
  }, [post.postId]);

  const handleReaction = async () => {
    if (!consumeReaction()) return;
    const newCount = reactionCount + 1;
    setReactionCount(newCount);
    setRemaining((n) => Math.max(0, n - 1));
    onReaction?.(post.postId, newCount); // 親の allPosts にも反映
    await addReaction(post.postId).catch(() => {});
  };

  const isShowingPhoto = view === 'photo';

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-50 mx-auto w-full max-w-lp rounded-t-2xl bg-white shadow-xl"
      style={{ maxHeight: '80dvh' }}
    >
      {/* ── ヘッダー ── */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
        <div className="flex flex-col">
          <div className="flex items-center gap-2">
            <p className="font-bold text-gray-800">{post.nickname || '匿名さん'}</p>
          </div>
          <div className="flex items-center gap-2">
            <p className="text-xs text-gray-400 mt-0.5">{formatRemaining(post)}</p>
            {post.isSuper && (
              <span className="text-xs font-bold text-green-600 bg-green-50 px-2 py-0.5 rounded-full">すっごーい！足あと</span>
            )}
          </div>
        </div>
        <button
          onClick={onClose}
          className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200 transition-colors"
        >
          ✕
        </button>
      </div>

      {/* ── メイン画像エリア ── */}
      <div className="relative aspect-square w-full bg-gray-100 overflow-hidden">
        {/* APIロード中スケルトン */}
        {detailLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
            <div className="h-10 w-10 animate-spin rounded-full border-4 border-gray-300 border-t-amber-500" />
          </div>
        )}

        {/* 写真 */}
        {!detailLoading && detail?.photoUrl && (
          <div
            className="absolute inset-0 transition-opacity duration-300"
            style={{ opacity: isShowingPhoto ? 1 : 0, pointerEvents: isShowingPhoto ? 'auto' : 'none' }}
          >
            <ImageWithLoader
              src={detail.photoUrl}
              alt="足あと写真"
              className="w-full h-full object-cover"
              containerClassName="w-full h-full"
            />
          </div>
        )}

        {/* お絵描き */}
        {!detailLoading && detail?.handwritingUrl && (
          <div
            className="absolute inset-0 bg-amber-50 transition-opacity duration-300 flex items-center justify-center"
            style={{ opacity: !isShowingPhoto ? 1 : 0, pointerEvents: !isShowingPhoto ? 'auto' : 'none' }}
          >
            <ImageWithLoader
              src={detail.handwritingUrl}
              alt="お絵描き"
              className="w-full h-full object-contain p-4"
              containerClassName="w-full h-full"
            />
          </div>
        )}

        {/* ── 右下: 切り替えサムネイルボタン ── */}
        {!detailLoading && detail?.handwritingUrl && (
          <button
            onClick={() => setView(isShowingPhoto ? 'sign' : 'photo')}
            className="absolute bottom-3 right-3 h-16 w-16 overflow-hidden rounded-xl border-2 border-white shadow-lg transition-transform active:scale-95"
            aria-label={isShowingPhoto ? 'お絵描きを見る' : 'フォトに戻る'}
            style={{ background: isShowingPhoto ? '#fef9c3' : '#f3f4f6' }}
          >
            {isShowingPhoto ? (
              /* お絵描きのサムネイル */
              <img
                src={detail.handwritingUrl}
                alt="お絵描きのサムネイル"
                className="h-full w-full object-contain p-1"
              />
            ) : (
              /* 写真のサムネイル */
              <img
                src={detail.photoUrl}
                alt="フォトのサムネイル"
                className="h-full w-full object-cover"
              />
            )}
          </button>
        )}
      </div>

      {/* ── フッター：「の」リアクション ── */}
      <div className="flex items-cente justify-center gap-3 px-4 py-3">
        <button
          onClick={handleReaction}
          className="flex items-center gap-1.5 px-2 py-2 font-bold text-amber-700 transition active:scale-95"
          aria-label="「の」リアクションを送る"
        >
          {/* SVGの余白をクロップ: circle center=(420,467) r=214 in 818×1350 */}
          <div style={{ width: 60, height: 32, overflow: 'hidden', position: 'relative', flexShrink: 0 }}>
            <img
              src="/images/kemono_no.svg"
              alt="の"
              style={{ position: 'absolute', width: 61, height: 'auto', top: -19, left: -0 }}
            />
          </div>
          <span className="text-l">{reactionCount}</span>
        </button>
        {remaining === 0 && (
          <p className="text-xs text-gray-400">本日の上限に達しました</p>
        )}
      </div>
    </div>
  );
};

export default FootprintDetailSheet;
